import moment from 'moment-timezone';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useGuestExperiences } from '../../FormSteps/hooks/useGuestExperiences';

import { BlockSelectField } from '@common/components/FormElements/BlockSelectField';
import { Spinner } from '@common/components/Spinner/Spinner';
import { useTranslations } from '@lang';
import { RestaurantsResponseWithSingleConfig } from '@store/types';
import { getLeavetime, parseTime, parseTimeslotsNew } from '@utils/helpers';

import { isOverlapping } from '../../helpers/isTimeslotOverlapping';
import { usePackageConfiguration } from '../../contexts';

import { Title4 } from '@common/components';
import { Checkbox } from '@common/components/Checkbox/Checkbox';
import { useGetTablesPage } from '@features/tables/hooks/useGetTablesPage';
import { TimeSlotsType } from '@features/tables/schemas/TablesAvailability.schema';
import { TimeSlotOptionHOC } from '@features/spa/pages/SpaDay/SpaPackageModal/SpaPackageAvailability/DateTimeSelector/TimeslotSelector/TimeSlotOptionHOC';
import { some } from 'lodash';
import { Separator, TablesSingleRestaurantContainer } from '../../FormSteps/FormSteps.styles';
import { DiningOption, PackagesFormValues, PackageType } from '../../types';
import {
  CheckboxWrapper,
  FieldContainer,
  FieldSpacer,
  InputContainer,
  ReturnMessage,
} from './TableForm.styles';
import { DaySelector } from '@common/components/DaySelector';

const isPromotionRequired = (timeslot) => timeslot && !timeslot.hasStandardAvailability;

type PromotionOption = {
  value: {
    id: string | number;
    timeslotReservationId: string | undefined;
  };
  label: string;
};

type TableFormProps = {
  restaurants: RestaurantsResponseWithSingleConfig[];
  timeslots: TimeSlotsType;
  isFetching: boolean;
  promotions: PromotionOption[];
  optionIndex: number;
  hasMenuRestrictions?: boolean;
  option: DiningOption;
};
const TimeSlotOption = (props) => <TimeSlotOptionHOC parentType="Option" {...props} />;
const TimeSlotValue = (props) => <TimeSlotOptionHOC parentType="SingleValue" {...props} />;

export const TableForm = ({
  restaurants,
  timeslots,
  isFetching,
  promotions,
  optionIndex,
  hasMenuRestrictions,
  option,
  ...rest
}: TableFormProps): JSX.Element => {
  const { t } = useTranslations();

  const { property, language, journeyParams, packageType } = usePackageConfiguration();

  const { control, setValue, watch, resetField } = useFormContext<PackagesFormValues>();
  const { adults, children } = journeyParams;

  const { data: tablesData } = useGetTablesPage(property?.id, language);
  const dietaryRequirements = tablesData?.dietaryRequirements || [];
  const celebrationOptions = tablesData?.celebrationOptions || [];

  const hasDietaryRequirements =
    watch(`tables.restaurants.${optionIndex}.hasDietaryRequirements`) || false;
  const hasCelebrationOptions =
    watch(`tables.restaurants.${optionIndex}.hasCelebrationOptions`) || false;

  useEffect(() => {
    setValue(`tables.restaurants.${optionIndex}.adults`, adults);
    setValue(`tables.restaurants.${optionIndex}.children`, children);
  }, []);

  const visitDate = watch(`tables.restaurants.${optionIndex}.visitDate`);
  const visitDateRangeStart =
    watch(`tables.restaurants.${optionIndex}.visitDateRangeStart`) || journeyParams.fromDate;
  const selectedTimeslot = watch(`tables.restaurants.${optionIndex}.timeslot`);
  const promotion = watch(`tables.restaurants.${optionIndex}.promotion`);
  const promotionId = promotion?.id;
  const guestExperiences = useGuestExperiences();
  const optionName = option.name;
  const dayNumber = option.dayNumber || 0;
  const delayAfter = option.delayAfter || 0;
  const availableDates = (option.dayNumberRange || [option.dayNumber || 0]).map((dayNum) =>
    moment(journeyParams.fromDate).add(dayNum, 'days').format('YYYY-MM-DD')
  );
  const dateSelectable =
    packageType !== PackageType.REPEATING || (option.dayNumberRange?.length ?? 0) > 1;

  useEffect(() => {
    if (selectedTimeslot && !promotions.length && promotion?.id !== null) {
      setValue(`tables.restaurants.${optionIndex}.promotion`, {
        id: null,
        name: null,
        timeslotReservationId: null,
      });
    }

    if (
      selectedTimeslot &&
      promotions.length === 1 &&
      !promotion &&
      !isPromotionRequired(selectedTimeslot)
    ) {
      setValue(`tables.restaurants.${optionIndex}.promotion`, {
        id: null,
        name: null,
        timeslotReservationId: null,
      });
    }

    if (selectedTimeslot && promotions.length === 1 && !promotion && hasMenuRestrictions) {
      setValue(`tables.restaurants.${optionIndex}.promotion`, {
        id: String(promotions[0].value.id),
        name: promotions[0].label,
        timeslotReservationId: promotions[0].value.timeslotReservationId,
      });
    }
  }, [promotions, selectedTimeslot, promotion, setValue, optionIndex, hasMenuRestrictions]);

  useEffect(() => {
    if (!visitDate && dateSelectable) {
      const initialDate = availableDates[0];
      setValue(`tables.restaurants.${optionIndex}.visitDate`, initialDate);
    } else if (!visitDate && !dateSelectable) {
      setValue(
        `tables.restaurants.${optionIndex}.visitDate`,
        moment(visitDateRangeStart).add(dayNumber, 'days').format('YYYY-MM-DD')
      );
    }
  }, [
    visitDate,
    availableDates,
    setValue,
    optionIndex,
    visitDateRangeStart,
    dayNumber,
    dateSelectable,
  ]);

  const timeSlotOptions: { value: string; label: string; date: string }[] = parseTimeslotsNew(
    timeslots,
    visitDate
  );

  const existingTimeslots = (
    (Array.isArray(guestExperiences) &&
      guestExperiences
        .filter((experience) => Array.isArray(experience?.bookings))
        .flatMap((experience) => experience.bookings)) ||
    []
  ).filter((booking) => booking && booking.dayNumber === dayNumber);

  const filteredTimeSlotOptions = timeSlotOptions.filter(
    (timeslot) =>
      !existingTimeslots ||
      !existingTimeslots.some(
        (existingTimeslot) =>
          existingTimeslot &&
          isOverlapping(
            {
              visitDate: visitDate!,
              startTime: moment(timeslot.value).format('HH:mm'),
              endTime: moment(timeslot.value).format('HH:mm'),
              delayAfter: delayAfter,
              dayNumber,
            },
            existingTimeslot
          )
      )
  );

  const leaveTime = getLeavetime(selectedTimeslot, promotionId);
  const handleDateChange = (selectedDate: string) => {
    setValue(`tables.restaurants.${optionIndex}.visitDate`, selectedDate);
    resetField(`tables.restaurants.${optionIndex}.timeslot`);
    resetField(`tables.restaurants.${optionIndex}.promotion`);
  };

  return (
    <>
      <Spinner isActive={isFetching} />

      <form {...rest}>
        {restaurants.length ? (
          <>
            {restaurants.length === 1 ? (
              <TablesSingleRestaurantContainer>
                <Title4>
                  {optionName ? `${optionName} - ` : ''}
                  {restaurants[0].label}
                </Title4>
                <Separator />
              </TablesSingleRestaurantContainer>
            ) : (
              <Controller
                name={`tables.restaurants.${optionIndex}.restaurant`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <BlockSelectField
                    label={t('tables.restaurantSelect')}
                    options={restaurants}
                    value={restaurants.find((rest) => rest.value === value?.configName)}
                    data-testid={`tables-form-restaurant-dropdown`}
                    onChange={({ restaurantName, value, config }) =>
                      onChange({ restaurantName, configName: value, id: config.id })
                    }
                    isRequired
                    placeholder="Select..."
                  />
                )}
              />
            )}
            {dateSelectable && (
              <FieldContainer>
                <FieldSpacer>
                  <DaySelector
                    availableDates={availableDates}
                    startDate={journeyParams.fromDate}
                    endDate={journeyParams.toDate || journeyParams.fromDate}
                    currentSelection={visitDate}
                    onChange={handleDateChange}
                  />
                </FieldSpacer>
              </FieldContainer>
            )}
            <FieldContainer>
              <FieldSpacer>
                {timeslots?.length && visitDate && !isFetching ? (
                  <InputContainer>
                    <Controller
                      name={`tables.restaurants.${optionIndex}.timeslot`}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <BlockSelectField
                          key={`${optionIndex}-${visitDate}`}
                          components={{
                            Option: TimeSlotOption,
                            SingleValue: TimeSlotValue,
                          }}
                          options={filteredTimeSlotOptions}
                          value={filteredTimeSlotOptions.find((ts) => ts.value === value?.timeSlot)}
                          onChange={(val) =>
                            onChange(timeslots.find((ts) => ts.timeSlot === val.value))
                          }
                          placeholder={t('tables.timeSelect')}
                          useNativeDropdownOnMobile={false}
                        />
                      )}
                    />
                  </InputContainer>
                ) : (
                  <></>
                )}
              </FieldSpacer>
              <FieldSpacer>
                <InputContainer>
                  {selectedTimeslot &&
                  hasMenuRestrictions &&
                  promotions?.length === 1 &&
                  promotion ? (
                    <Controller
                      name={`tables.restaurants.${optionIndex}.promotion`}
                      control={control}
                      render={() => (
                        <BlockSelectField
                          key={`${optionIndex}-${visitDate}`}
                          options={promotions}
                          value={promotions[0]}
                          onChange={() => {}}
                          placeholder={t('tables.menuSelect')}
                          isDisabled={true}
                          useNativeDropdownOnMobile={false}
                        />
                      )}
                    />
                  ) : promotions?.length ? (
                    <Controller
                      name={`tables.restaurants.${optionIndex}.promotion`}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <BlockSelectField
                          key={`${optionIndex}-${visitDate}`}
                          options={promotions}
                          value={promotions.find((p) => p.value.id === value?.id)}
                          onChange={(val) =>
                            val
                              ? onChange({
                                  id: String(val.value.id),
                                  name: val.label,
                                  timeslotReservationId: val.value.timeslotReservationId,
                                })
                              : onChange(null)
                          }
                          placeholder={`${t('tables.menuSelect')} ${
                            !isPromotionRequired(selectedTimeslot) && !hasMenuRestrictions
                              ? t('common.optional')
                              : ''
                          }`}
                          isRequired={isPromotionRequired(selectedTimeslot) || hasMenuRestrictions}
                          isClearable={
                            !isPromotionRequired(selectedTimeslot) && !hasMenuRestrictions
                          }
                          useNativeDropdownOnMobile={false}
                        />
                      )}
                    />
                  ) : (
                    <></>
                  )}
                </InputContainer>
              </FieldSpacer>
              <FieldSpacer>
                {selectedTimeslot && selectedTimeslot.isLeaveTimeRequired && (
                  <ReturnMessage>
                    {t('tables.returnInfo', { time: parseTime(leaveTime) })}
                  </ReturnMessage>
                )}
              </FieldSpacer>
              {some(dietaryRequirements) && (
                <>
                  <CheckboxWrapper>
                    <Controller
                      name={`tables.restaurants.${optionIndex}.hasDietaryRequirements`}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id={`dietary-requirements-checkbox-${optionIndex}`}
                          label={t('tables.dietaryRequirementsText')}
                          checked={field.value || false}
                          onChange={(e) => {
                            field.onChange(e);
                            if (!e.target.checked) {
                              setValue(`tables.restaurants.${optionIndex}.dietaryRequirements`, []);
                            }
                          }}
                        />
                      )}
                    />
                  </CheckboxWrapper>
                  {hasDietaryRequirements && (
                    <FieldSpacer>
                      <InputContainer>
                        <Controller
                          name={`tables.restaurants.${optionIndex}.dietaryRequirements`}
                          control={control}
                          render={({ field }) => (
                            <BlockSelectField
                              isMulti
                              id={`dietary-requirements-select-${optionIndex}`}
                              options={dietaryRequirements.map((option) => ({
                                value: option,
                                label: option,
                              }))}
                              onChange={(selectedOptions) => {
                                field.onChange(selectedOptions.map((opt) => opt.value));
                              }}
                              value={field.value?.map((value) => ({ value, label: value })) || []}
                              noOptionsMessage={() => t('tables.noDietaryRequirementOptions')}
                            />
                          )}
                        />
                      </InputContainer>
                    </FieldSpacer>
                  )}
                </>
              )}

              {some(celebrationOptions) && (
                <>
                  <CheckboxWrapper>
                    <Controller
                      name={`tables.restaurants.${optionIndex}.hasCelebrationOptions`}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id={`celebration-options-checkbox-${optionIndex}`}
                          label={t('tables.specialOccasionText')}
                          checked={field.value || false}
                          onChange={(e) => {
                            field.onChange(e);
                            if (!e.target.checked) {
                              setValue(`tables.restaurants.${optionIndex}.celebrationOptions`, []);
                            }
                          }}
                        />
                      )}
                    />
                  </CheckboxWrapper>
                  {hasCelebrationOptions && (
                    <FieldSpacer>
                      <InputContainer>
                        <Controller
                          name={`tables.restaurants.${optionIndex}.celebrationOptions`}
                          control={control}
                          render={({ field }) => (
                            <BlockSelectField
                              isMulti
                              id={`celebration-options-select-${optionIndex}`}
                              options={celebrationOptions.map((option) => ({
                                value: option,
                                label: option,
                              }))}
                              onChange={(selectedOptions) => {
                                field.onChange(selectedOptions.map((opt) => opt.value));
                              }}
                              value={field.value?.map((value) => ({ value, label: value })) || []}
                              noOptionsMessage={() => t('tables.noCelebrationOptions')}
                            />
                          )}
                        />
                      </InputContainer>
                    </FieldSpacer>
                  )}
                </>
              )}
            </FieldContainer>
          </>
        ) : (
          <Spinner isActive={true} />
        )}
      </form>
    </>
  );
};
